import {useEffect} from 'react'
import {Controller} from 'react-hook-form'
import {ValidationError} from 'joi'

import {selectTheme, selectWorkspaces} from '../../store'
import {v, useForm, useCurrentWorkspaceState} from '../../lib'
import {AboutProjectIcon} from '../../asset/image'
import {Input, LabelBox, Textarea} from '..'
import {ProjectContent} from '../../model'

export type AboutProjectValue = Pick<ProjectContent, 'name' | 'investigator' | 'organization' | 'description'>

export interface AboutProjectBlockPropsT {
  aboutProjectValue: AboutProjectValue
  onChange: (values: AboutProjectValue) => void
  onValidated: () => void
  onError: (errors: ValidationError) => void
}

export const AboutProjectBlock = (props: AboutProjectBlockPropsT) => {
  const {pad, color, fontWeight} = selectTheme()
  const workspacesState = selectWorkspaces()
  const {workspaceId} = useCurrentWorkspaceState()
  const role = workspacesState.roleInWorkspaces[workspaceId ?? ''] || 'owner'

  const enum WordCountMaximum {
    projectName = 50,
    projectInvestigator = 50,
    organization = 50,
    introduction = 250,
  }

  const {onChange, onValidated, onError, aboutProjectValue} = props

  const schema = v.object<Required<AboutProjectValue>>({
    name: v.string().min(1).max(WordCountMaximum.projectName).required(),
    investigator: v.string().min(1).max(WordCountMaximum.projectInvestigator).required(),
    organization: v.string().min(1).max(WordCountMaximum.organization).required(),
    description: v.string().min(1).max(WordCountMaximum.introduction).required(),
  })

  const {control, getValues, watch, reset} = useForm<AboutProjectValue>({
    schema,
    shouldFocusError: false,
  })

  useEffect(() => {
    if (aboutProjectValue && JSON.stringify(getValues()) !== JSON.stringify(aboutProjectValue)) {
      reset(aboutProjectValue)
    }
  }, [aboutProjectValue, reset, getValues])

  const nameField = watch('name')
  const investigatorField = watch('investigator')
  const organizationField = watch('organization')
  const descriptionField = watch('description')

  useEffect(() => {
    const form: AboutProjectValue = {
      name: nameField,
      investigator: investigatorField,
      organization: organizationField,
      description: descriptionField,
    }
    onChange(form)

    const result = schema.validate(form)
    if (!result.error) {
      onValidated()
    } else {
      onError(result.error)
    }
  }, [nameField, investigatorField, organizationField, descriptionField])

  return (
    <div
      css={{
        display: 'flex',
        background: color.white,
        borderRadius: '5px;',
        width: '55vw',
        marginBottom: pad.large,
        boxShadow: '0px 4px 12px 0px #D4D4D440',
      }}>
      {/* left */}
      <div
        css={{
          padding: pad.large,
          borderRight: `1px solid ${color.grey_100}`,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          width: '26%',
        }}>
        <div>
          <div
            css={{
              width: '40px',
              height: '40px',
              background: color.background,
              borderRadius: '5px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: pad.large,
            }}>
            <img src={AboutProjectIcon} width='24px' alt='' aria-hidden />
          </div>
          <h2
            css={{
              fontSize: '18px',
              fontWeight: fontWeight.thick,
              margin: 0,
              marginBottom: pad.medium,
            }}>
            About Project
          </h2>

          <p css={{lineHeight: '19px', fontWeight: fontWeight.regular}}>
            Provide more details about your project for participants.
          </p>
        </div>
      </div>

      {/* right */}
      <div css={{padding: pad.xl, width: '74%'}}>
        <p
          css={{
            fontSize: '14px',
            fontWeight: fontWeight.medium,
            marginBottom: pad.xl,
          }}>
          Project information as seen by participants:
        </p>

        {/* projectName — LabelBox must wrap Input directly so htmlFor/id association works */}
        <Controller
          control={control}
          name='name'
          render={({field: {name, value, onChange}}) => (
            <LabelBox
              {...{
                label: 'Project Name',
                required: true,
                leftCharacters: WordCountMaximum.projectName - (nameField?.length ?? 0),
              }}>
              <Input
                readOnly={role === 'viewer'}
                {...{
                  name,
                  value: value ?? '',
                  onChange,
                }}
                maxLength={WordCountMaximum.projectName}
                placeholder='Labfront Sleep Study'
                css={{marginBottom: '27px'}}
              />
            </LabelBox>
          )}
        />

        {/* Investigator */}
        <Controller
          control={control}
          name='investigator'
          render={({field: {name, value, onChange}}) => (
            <LabelBox
              {...{
                label: 'Principal Investigator',
                required: true,
                leftCharacters: WordCountMaximum.projectInvestigator - (investigatorField?.length ?? 0),
              }}>
              <Input
                readOnly={role === 'viewer'}
                {...{
                  name,
                  value: value ?? '',
                  onChange,
                }}
                maxLength={WordCountMaximum.projectInvestigator}
                placeholder='Dr. Luna Shiba'
                css={{marginBottom: '27px'}}
              />
            </LabelBox>
          )}
        />

        {/* Organization */}
        <Controller
          control={control}
          name='organization'
          render={({field: {name, value, onChange}}) => (
            <LabelBox
              {...{
                label: 'Organization or Institution',
                required: true,
                leftCharacters: WordCountMaximum.organization - (organizationField?.length ?? 0),
              }}>
              <Input
                readOnly={role === 'viewer'}
                {...{
                  name,
                  value: value ?? '',
                  onChange,
                }}
                maxLength={WordCountMaximum.organization}
                placeholder='Labfront'
                css={{marginBottom: '27px'}}
              />
            </LabelBox>
          )}
        />

        {/* Introduction */}
        <Controller
          control={control}
          name='description'
          render={({field: {name, value, onChange}}) => (
            <LabelBox
              {...{
                label: 'Introduction',
                required: true,
                leftCharacters: WordCountMaximum.introduction - (descriptionField?.length ?? 0),
              }}>
              <Textarea
                readOnly={role === 'viewer'}
                {...{
                  name,
                  value: value ?? '',
                  onChange,
                }}
                maxLength={WordCountMaximum.introduction}
                placeholder='Briefly introduce your study here.'
                rows={4}
              />
            </LabelBox>
          )}
        />
      </div>
    </div>
  )
}
